import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Document } from '../../shared/document.model';
import { RailroadTransactionCarData } from '../car-repair-billing/railroad-transaction-car-data.model';
import { Customer } from '../customer/customer.model';
import { SearchRequest } from '../program-work/search-request.model';
import { SearchResult } from '../program-work/search-result.model';
import { Railcar } from '../railcar/railcar.model';
import { Contact } from '../facility/contact.model';
import { TrackAndTraceResult } from '../track-and-trace/track-and-trace-result.model';
import { BulkServiceEventTransfer } from './bulk-service-event-wizard/bulk-service-event-transfer.model';
import { ServiceEventReason } from './service-event-reason.model';
import { ServiceEventState } from './service-event-state.model';
import { ServiceEventType } from './service-event-type.model';
import { ServiceEvent } from './service-event.model';
import { ERDChangeReason, ERDReason } from './erd-reason.model';

@ Injectable({providedIn: 'root'})
export class ServiceEventService {
	private serviceTypesUrl: any;
	private url: string;

	constructor(private httpClient: HttpClient) {
		this.url = 'api/serviceEvent';
	}

	public search(searchText: SearchRequest): Observable<SearchResult> {
		return this.httpClient.post<SearchResult>(this.url + '/search', searchText);
	}

	public getServiceEvent(id: number): Observable<ServiceEvent> {
		return this.httpClient.get<ServiceEvent>(this.url + '/' + id);
	}

	public saveServiceEvent(serviceEvent: ServiceEvent): Observable<ServiceEvent> {
		return this.httpClient.post<ServiceEvent>(this.url, serviceEvent);
	}

	public publishServiceEvent(serviceEventId: number, sendShoppingNotice: boolean = true, sendCustomerNotice: boolean = true): Observable<string[]> {
		return this.httpClient.post<string[]>(this.url + '/PublishServiceEvent?serviceEventId=' + serviceEventId + '&sendShoppingNotice=' +
			sendShoppingNotice + '&sendCustomerNotice=' + sendCustomerNotice, null);
	}

	public publishBulkServiceEvents(serviceEventIds: number[], emailBody: string): Observable<any> {
		return this.httpClient.post<any>(this.url + '/publishBulkServiceEvent?emailContent=' + emailBody, serviceEventIds);
	}

	public publishScrappingEvent(serviceEventId: number, sendEmailToScrapLocation: boolean = true, sendShopDispoEmailForScrap: boolean = true,
		traceRailcar: boolean = true): Observable<string[]> {
		return this.httpClient.post<string[]>(this.url + '/PublishServiceEvent?serviceEventId=' + serviceEventId + '&sendEmailToScrapLocation=' +
			sendEmailToScrapLocation + '&sendShopDispoEmailForScrap=' + sendShopDispoEmailForScrap + '&traceRailcar=' + traceRailcar, null);
	}

	public bulkCreateServiceEvents(data: BulkServiceEventTransfer, isScrapSe:boolean = false,programWorkId:number): Observable<ServiceEvent[]> {
		return this.httpClient.post<ServiceEvent[]>(this.url + '/BulkCreateServiceEvents?isScrapSe=' + isScrapSe +'&programWorkId='+programWorkId, data);
	}

	public saveServiceEvents(serviceEvents: ServiceEvent[]): Observable<Object[]> {
		return this.httpClient.post<Object[]>(this.url + '/SaveServiceEvents', serviceEvents);
	}

	public getServiceEventTypes(): Observable<ServiceEventType[]> {
		return this.httpClient.get<ServiceEventType[]>(this.url + '/type');
	}

	public getServiceEventStates(): Observable<ServiceEventState[]> {
		return this.httpClient.get<ServiceEventState[]>(this.url + '/state');
	}

	public getServiceEventReasons(): Observable<ServiceEventReason[]> {
		return this.httpClient.get<ServiceEventReason[]>(this.url + '/reason');
	}

	public getServiceEventsByRailcar(id: number): Observable<ServiceEvent[]> {
		return this.httpClient.get<ServiceEvent[]>(this.url + '/railcar/' + id);
	}

	public getOwnershipByRailcar(railcarId: number, shipmentDate: Date): Observable<any> {
		return this.httpClient.get<any>(this.url + `/getOwnershipByRailcar?railcarId=
			${railcarId}&shipmentDate=${shipmentDate.toLocaleDateString()}`);
	}

	public getOrOpenServiceEvent(id: number): Observable<ServiceEvent> {
		return this.httpClient.get<ServiceEvent>(this.url + '/getoropen/' + id);
	}

	public generateServiceEvent(id: number): Observable<ServiceEvent> {
		return this.httpClient.get<ServiceEvent>(this.url + '/generateServiceEvent/' + id);
	}
	
	public getActiveEventsByRailcarAsync(id: number): Observable<ServiceEvent> {
		return this.httpClient.get<ServiceEvent>(this.url + '/getActiveEventsByRailcarAsync/' + id);
	}
	public getServiceEventsByProgramWork(id: number): Observable<ServiceEvent[]> {
		return this.httpClient.get<ServiceEvent[]>(this.url + '/program/' + id);
	}

	public getServiceEventsByFacility(id: number): Observable<ServiceEvent[]> {
		return this.httpClient.get<ServiceEvent[]>(this.url + '/GetOpenServiceEventsByFacility?facilityId=' + id);
	}

	public getPublishedShoppingnoticePdf64ById(id: number): Observable<string> {
		return this.httpClient.get(this.url + '/shoppingNoticeById/' + id, { responseType: 'text' });
	}

	public getPublishedShoppingnoticePdf64(serviceEvent: ServiceEvent): Observable<string> {
		return this.httpClient.get(this.url + '/shoppingnotice?serviceevent=' + serviceEvent, { responseType: 'text' });
	}

	public getPublishedCustomernoticePdf64ById(id: number): Observable<string> {
		return this.httpClient.get(this.url + '/customerNoticeById/' + id, { responseType: 'text' });
	}

	public getResponsibilityItemsPdf(id: number, code: string): Observable<string> {
		return this.httpClient.get(this.url + '/GetResponsibilityItemsPdf/' + id + '/' + code, { responseType: 'text' });
	}

	public enrollCarTrace(seId: number, traceType: string): Observable<string> {
		let endPoint: string = '';

		switch (traceType.toLocaleUpperCase()) {
			case 'TOSHOP':
			endPoint = '/EnrollToShopCartrace/';
			break;

			case 'FROMSHOP':
			endPoint = '/EnrollFromShopCartrace/';
			break;
		}

		if (!endPoint) {
			throw new Error('Invalid trace type');
		}
		console.log(this.url + endPoint + seId + traceType);
		return this.httpClient.post<string>(this.url + endPoint, seId);
	}

	public getSalesforceCustomerAccount(sfId: string): Observable<Customer> {
		return this.httpClient.get<Customer>(this.url + '/getCustomerAccount?sfId=' + sfId);
	}

	public getShopImageThumbnails(seId: number, getStagedImages?: boolean): Observable<Document[]> {
		return this.httpClient.get<Document[]>(`${this.url}/getShopImageThumbnails?serviceEventId=${seId}${getStagedImages == true ? "&getStagedImages=" + getStagedImages : ""}`);
	  }

	public getShopImage(fileName: string): Observable<Document> {
		return this.httpClient.get<Document>(this.url + '/getShopImage?fileName=' + fileName);
	}

	public getShopImages(seId: number): Observable<Document[]> {
		return this.httpClient.get<Document[]>(this.url + '/getShopImages?serviceEventId=' + seId);
	}

	public getOpenServiceEventsByRailcarList(railcars: Railcar[]): Observable<ServiceEvent[]> {
		return this.httpClient.get<ServiceEvent[]>(this.url + '/getOpenServiceEventsByRailcarList?railcarIds=' +
			railcars.map(x => x.id).join(','));
	}

	public getOpenServiceEventsByCarmark(carmark: string): Observable<ServiceEvent[]> {
		return this.httpClient.get<ServiceEvent[]>(this.url + '/getOpenServiceEventsByCarmark?carmark=' +
			carmark);
	}
	
	public getLastNServiceEventsByRailcar(railcarid: number, take: number) {
		return this.httpClient.get<ServiceEvent[]>(this.url + '/getLastNServiceEventsByRailcar?railcarId=' +
			railcarid + '&take=' + take);
	}

	public isNoWorkCreated(crbHeaderId: number) {
		return this.httpClient.get<boolean>(this.url + '/isNoWorkCreated?crbHeaderId=' + crbHeaderId);
	}

	public traceRailcarsLastSighting(railcars: Railcar[]): Observable<TrackAndTraceResult[]> {
		return this.httpClient.get<TrackAndTraceResult[]>(this.url + '/traceRailcarsLastSighting?railcarIds=' +
			railcars.map(x => x.id).join(','));
	}

	public sendImages(toSend: any, serviceEventId: number): Observable<boolean> {
		return this.httpClient.post<boolean>(this.url + '/sendImages/' + serviceEventId, toSend);
	}

	public sendToCustomer(serviceEventId: number): Observable<any> {
		return this.httpClient.put<any>(this.url + '/updateSentToCustomer/' + serviceEventId, null);
	}

	public getSePgmMissingFileMechComponents(seviceEvents: ServiceEvent[]): Observable<any[]> {
		return this.httpClient.post<any[]>(this.url + '/getSePgmMissingFileMechComponents', seviceEvents);
	}

	public getDestroyedDismantledServiceEvent(railcarId: number, type: string): Observable<RailroadTransactionCarData> {
		if (type === 'destroyed') {
			return this.httpClient.get<RailroadTransactionCarData>(this.url + '/getDestroyedServiceEvent?railcarId=' + railcarId);
		} else if (type === 'dismantled') {
			return this.httpClient.get<RailroadTransactionCarData>(this.url + '/getDismantledServiceEvent?railcarId=' + railcarId);
		} else {
			throw new Error('Invalid Dismantle type');
		}
	}

	public isRailincTransaction(serviceEventId: number): Observable<boolean> {
		return this.httpClient.get<boolean>(this.url + '/isRailincTransaction/?id=' + serviceEventId);
	}

	public bulkFetchServiceEvents(railcarIds: string): Observable<ServiceEvent[]> {
		return this.httpClient.get<ServiceEvent[]>(this.url + '/bulkFetchServiceEvents?railcarIds=' + railcarIds);
	}

	public discardServiceEvent(id: number): Observable<any> {
		return this.httpClient.delete<any>(this.url + `/discardServiceEvent?id=${id}`);
	}

	public setEstimateToNoWorkStayingAtShop(serviceEventId: number, flatFee: number): Observable<any> {
		var params = '/setEstimateToNoWork?serviceEventId=' + serviceEventId + '&isStayingAtShop=true';
		if (!!flatFee){
			params += '&flatFee=' + flatFee;
		}
		return this.httpClient.put(this.url + params, null);
	}

	public setEstimateToNoWorkNotStayingAtShop(serviceEventId: number, storageLocationId: number, routeToStorageLocation: string,
		dispositionDate: Date, flatFee: number, isScrapped: boolean): Observable<any> {
		dispositionDate?.setHours(6);
		let params = '';
		if (storageLocationId && routeToStorageLocation) {
			if (dispositionDate) {
				params = dispositionDate ?
					'/setEstimateToNoWork?serviceEventId=' + serviceEventId + '&isStayingAtShop=false' + '&storageLocationId=' + storageLocationId +
						'&routeToStorageLocation=' + routeToStorageLocation + '&dispositionDate=' + dispositionDate?.toISOString() + '&isScrapped=' + isScrapped
					: '/setEstimateToNoWork?serviceEventId=' + serviceEventId + '&isStayingAtShop=false' + '&storageLocationId=' + storageLocationId +
						'&routeToStorageLocation=' + routeToStorageLocation + '&isScrapped=' + isScrapped;
			}
		} else {
			params = dispositionDate ?
				'/setEstimateToNoWork?serviceEventId=' + serviceEventId + '&isStayingAtShop=false' +
					'&dispositionDate=' + dispositionDate?.toISOString() + '&isScrapped=' + isScrapped
				: '/setEstimateToNoWork?serviceEventId=' + serviceEventId + '&isStayingAtShop=false' + '&isScrapped=' + isScrapped;
		}
		if (!!flatFee) {
			params += '&flatFee=' + flatFee;
		}
		return this.httpClient.put(this.url + params, null);
	}

	public designateForScrapping(serviceEventId: number, scrapLocationId: number, routeToScrapLocation: string,
		dispositionDate: Date, zone: string, track: string, isCfclFreightPayer: boolean, isEndBillingOnLease: boolean): Observable<any> {
		return this.httpClient.put(this.url + '/designateForScrapping?serviceEventId=' + serviceEventId  + '&scrapLocationId=' +
			scrapLocationId + '&routeToScrapLocation=' + routeToScrapLocation + '&dispositionDate=' + dispositionDate.toLocaleDateString() +
			'&zone=' + zone + '&track=' + track + '&isCfclFreightPayer=' + isCfclFreightPayer + '&isEndBillingOnLease=' + isEndBillingOnLease, null);
	}

	public cancelServiceEvent(id: number): Observable<any> {
		return this.httpClient.delete<any>(this.url + `/cancelServiceEvent?id=${id}`)
	}
	public removeServiceEventImage(serviceEventId: number, SEdocuments: any): Observable<boolean> {
		return this.httpClient.post<boolean>(this.url + '/RemoveServiceEventImage/' + serviceEventId, SEdocuments);
	}

	public removeProgramsFromServiceEvent(serviceEventId: number, railcarId: number, programWorkIds: number[]): Observable<any> {
		return this.httpClient.post<any>(this.url + '/removeProgramsFromServiceEvent?serviceEventId=' + serviceEventId + '&railcarId=' + railcarId, programWorkIds);
	}

	public addProgramsToServiceEvent(serviceEventId: number, railcarId: number, primaryProgramId: number, programWorkIds: number[]): Observable<any> {
		return this.httpClient.post<any>(this.url + '/addProgramsToServiceEvent?serviceEventId=' + serviceEventId + '&railcarId=' + railcarId + '&primaryProgramId=' + primaryProgramId, programWorkIds);
	}

	public updatePrimaryProgram(serviceEventId: number, primaryProgramId: number): Observable<any> {
		return this.httpClient.post<any>(this.url + '/updatePrimaryProgram?serviceEventId=' + serviceEventId, primaryProgramId);
	}

	public getServiceEventReasonsByName(id:number): Observable<ServiceEventReason[]> {
		return this.httpClient.get<ServiceEventReason[]>(this.url + '/reason/'+id);
	}
	public saveServiceEventReason(serviceEventReason: ServiceEventReason): Observable<any> {
		return this.httpClient.post<any>(this.url+'/SaveServiceEventReason', serviceEventReason);
	}
	public deleteServiceEventReason(id: number): Observable<any> {
		return this.httpClient.delete<any>(this.url+'/DeleteServiceEventReason?id='+ id);
	}
	public processDestroyedCar(serviceEventId: number, incidentDate: Date, acceptedDate: Date, dvAmount: number, badOrderAt: string): Observable<any> {
		return this.httpClient.put(this.url + '/ProcessDestroyedCar?serviceEventId=' + serviceEventId + '&incidentDate=' + incidentDate.toLocaleDateString() + '&acceptedDate=' + acceptedDate.toLocaleDateString() + '&dvAmount=' + dvAmount + '&badOrderAt=' + badOrderAt, null);
	}

	public getERDReasonCodes(): Observable<ERDReason[]> {
		return this.httpClient.get<ERDReason[]>(this.url + '/getERDReasons');
	}

	public getERDReasonsAgainstServiceEvent(serviceEventId: number): Observable<ERDChangeReason[]> {
		return this.httpClient.get<ERDChangeReason[]>(this.url + '/getERDReasonsAgainstServiceEvent?serviceEventId=' + serviceEventId);
	}
	public saveERDChangeReasonsAgainstServiceEvent(erdChangeReason: ERDChangeReason, sendERDUpdateEmailToCustomer : boolean): Observable<ERDChangeReason[]> {
		return this.httpClient.post<ERDChangeReason[]>(this.url + '/saveERDChangeReasonsAgainstServiceEvent?sendERDUpdateEmailToCustomer=' + sendERDUpdateEmailToCustomer , erdChangeReason);
	}
	public sendAttachmentsAndImagesToSEDetails(detailsToSend: any, serviceEventId: number, notes: string, includeEstimate:boolean): Observable<boolean> {
		return this.httpClient.post<boolean>(this.url + '/sendAttachmentsAndImagesToSEDetails?serviceEventId=' + serviceEventId + '&notes=' + notes + '&includeEstimate=' + includeEstimate, detailsToSend);
	}
}
